import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useOpportunities } from '../hooks/useSDI'
import { MarketTabsLayout } from '../components/sdi/shared/MarketTabsLayout'
import type { OpportunityItem } from '../hooks/sdi.types'
import { Target, X, TrendingDown, DollarSign, Download, ChevronRight, Plus, Wallet } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceArea, Cell,
} from 'recharts'

const fmtCompact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 })
const fmtPct = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1 })
const fmtEur = new Intl.NumberFormat('fr-FR', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 })

const TOP_N = 10

const clusterColors: Record<string, string> = {
  default: '#335cfc', plomberie: '#8b5cf6', sanitaire: '#06b6d4', urgence: '#f43f5e',
  prix: '#f59e0b', zahnarzt: '#10b981', reinigung: '#6366f1', kieferortho: '#ec4899',
}
function getColor(cluster: string | null): string { return clusterColors[cluster ?? 'default'] ?? clusterColors.default }

function QuadrantTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: OpportunityItem }> }) {
  if (!active || !payload?.[0]) return null
  const d = payload[0].payload
  return (
    <div className="bg-white border border-ink-border rounded-card p-3 shadow-elevated text-xs">
      <p className="font-semibold text-ink mb-1">{d.keyword}</p>
      {d.cluster_group && <p className="text-ink-faint mb-1">{d.cluster_group}</p>}
      <p>Demande: {fmtCompact.format(d.demand)}</p>
      <p>Capture: {fmtPct.format(d.capture_rate)}</p>
      <p>Score: {d.opportunity_score.toFixed(1)}</p>
      <p className="text-semantic-lost font-semibold">Perdu: {fmtEur.format(d.lost_value)}</p>
    </div>
  )
}

function OpportunityDrilldown({ item, onClose }: { item: OpportunityItem; onClose: () => void }) {
  const navigate = useNavigate()
  const { id: marketId } = useParams<{ id: string }>()

  return (
    <div className="fixed inset-y-0 right-0 w-[400px] bg-white border-l border-ink-border shadow-elevated z-40 animate-slide-in-right">
      <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border">
        <h3 className="text-sm font-semibold text-ink">Détail opportunité</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <p className="text-lg font-bold text-ink">{item.keyword}</p>
          {item.cluster_group && <p className="text-xs text-ink-faint mt-1">Cluster: {item.cluster_group}</p>}
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold-light text-xs font-semibold text-brick">
            Score: {item.opportunity_score.toFixed(1)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Demande', value: fmtCompact.format(item.demand), icon: Target },
            { label: 'Capture', value: fmtPct.format(item.capture_rate), icon: TrendingDown },
            { label: 'CPC haut', value: `${item.cpc_high.toFixed(2)} €`, icon: DollarSign },
            { label: 'Valeur perdue', value: fmtEur.format(item.lost_value), icon: Wallet },
          ].map((m) => (
            <div key={m.label} className="bg-paper rounded-lg p-3">
              <m.icon size={14} className="text-ink-faint mb-1" />
              <p className="text-sm font-bold text-ink">{m.value}</p>
              <p className="text-[10px] text-ink-muted">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-xs font-semibold text-red-700 mb-1">Opportunité estimée</p>
          <p className="text-2xl font-bold text-semantic-lost">{fmtEur.format(item.lost_value)}</p>
        </div>

        <div className="flex flex-col gap-2 pt-2 border-t border-ink-border">
          <button onClick={() => navigate(`/sdi/markets/${marketId}/budget`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg">
            <Wallet size={16} /> Simuler le budget
          </button>
          <button onClick={() => navigate(`/sdi/markets/${marketId}/keywords`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-brick border border-brick hover:bg-gold-light rounded-lg">
            <Target size={16} /> Explorer le cluster
          </button>
        </div>
      </div>
    </div>
  )
}

export function OpportunityMapPage() {
  const { id: marketId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: opps, isLoading } = useOpportunities(marketId!, 100)
  const [selectedKw, setSelectedKw] = useState<OpportunityItem | null>(null)
  const [minScore, setMinScore] = useState(0)

  const items = opps?.items ?? []
  const filteredItems = useMemo(() => items.filter((i) => i.opportunity_score >= minScore), [items, minScore])
  const fadedItems = useMemo(() => items.filter((i) => i.opportunity_score < minScore), [items, minScore])
  const topItems = filteredItems.slice(0, TOP_N)

  if (!isLoading && items.length < 3) {
    return (
      <MarketTabsLayout>
        <div className="bg-white rounded-card border border-ink-border p-12 text-center">
          <Target size={48} className="mx-auto text-ink-faint mb-4" />
          <h3 className="text-lg font-semibold text-ink mb-2">Données insuffisantes</h3>
          <p className="text-ink-muted max-w-md mx-auto">Ajoutez au moins 3 mots-clés avec des métriques pour activer la carte des opportunités.</p>
        </div>
      </MarketTabsLayout>
    )
  }

  const maxDemand = Math.max(...items.map((i) => i.demand), 100)
  const maxCapture = Math.max(...items.map((i) => i.capture_rate), 0.5)
  const midDemand = maxDemand / 2

  const exportCsv = () => {
    const header = ['keyword', 'cluster', 'demand', 'capture', 'score', 'lost_value']
    const rows = filteredItems.map((i) => [i.keyword, i.cluster_group ?? '', i.demand, i.capture_rate, i.opportunity_score, i.lost_value])
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n')
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = `sdi_opportunities_${marketId}.csv`; a.click()
  }

  return (
    <MarketTabsLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-ink">Carte des opportunités</h2>
            <p className="text-sm text-ink-faint">{filteredItems.length} mot(s)-clé(s) · Opportunité: {fmtEur.format(opps?.total_lost_value ?? 0)}</p>
          </div>
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink bg-paper-warm hover:bg-paper-warm rounded-lg">
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Score Filter Slider */}
        <div className="bg-white rounded-card border border-ink-border p-4 flex items-center gap-4">
          <span className="text-xs text-ink-muted whitespace-nowrap">Score min:</span>
          <input type="range" min={0} max={50} step={1} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))}
            className="flex-1 h-2 bg-paper-warm rounded-lg appearance-none cursor-pointer accent-gold" />
          <span className="text-xs font-bold text-brick w-8 text-right">{minScore}</span>
        </div>

        {/* Quadrant Chart */}
        <div className="bg-white rounded-card border border-ink-border p-5">
          <h3 className="text-sm font-semibold text-ink mb-4">Demande vs. Capture (taille = score)</h3>
          {isLoading ? <div className="h-[400px] bg-paper-warm rounded animate-pulse" /> : (
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" dataKey="capture_rate" tickFormatter={(v) => fmtPct.format(v)} tick={{ fontSize: 11, fill: '#94a3b8' }}
                  label={{ value: 'Taux de capture', position: 'bottom', fontSize: 11, fill: '#64748b', offset: 15 }} />
                <YAxis type="number" dataKey="demand" tickFormatter={(v) => fmtCompact.format(v)} tick={{ fontSize: 11, fill: '#94a3b8' }}
                  label={{ value: 'Demande', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#64748b' }} />
                <ZAxis type="number" dataKey="opportunity_score" range={[60, 400]} />
                <ReferenceArea x1={0} x2={0.30} y1={midDemand} y2={maxDemand * 1.1} fill="#fef2f2" fillOpacity={0.4}
                  label={{ value: 'Priorité max', fontSize: 10, fill: '#922B21' }} />
                <ReferenceArea x1={0.30} x2={maxCapture * 1.1} y1={midDemand} y2={maxDemand * 1.1} fill="#f0fdf4" fillOpacity={0.4}
                  label={{ value: 'Leader', fontSize: 10, fill: '#1E6B3A' }} />
                <ReferenceArea x1={0} x2={0.30} y1={0} y2={midDemand} fill="#fefce8" fillOpacity={0.4}
                  label={{ value: 'À explorer', fontSize: 10, fill: '#ca8a04' }} />
                <ReferenceArea x1={0.30} x2={maxCapture * 1.1} y1={0} y2={midDemand} fill="#f8fafc" fillOpacity={0.4}
                  label={{ value: 'Niche captée', fontSize: 10, fill: '#94a3b8' }} />
                <Tooltip content={<QuadrantTooltip />} />
                {/* Faded items below threshold */}
                {fadedItems.length > 0 && (
                  <Scatter data={fadedItems}>
                    {fadedItems.map((_, idx) => <Cell key={idx} fill="#cbd5e1" fillOpacity={0.15} />)}
                  </Scatter>
                )}
                {/* Active items */}
                <Scatter data={filteredItems} onClick={(d) => setSelectedKw(d as unknown as OpportunityItem)} cursor="pointer">
                  {filteredItems.map((item, idx) => (
                    <Cell key={idx} fill={getColor(item.cluster_group)} fillOpacity={0.75} stroke={getColor(item.cluster_group)} strokeWidth={1} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 mt-4">
            {[...new Set(items.map((i) => i.cluster_group).filter(Boolean))].map((cg) => (
              <span key={cg} className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getColor(cg) }} /> {cg}
              </span>
            ))}
          </div>
        </div>

        {/* Ranking Table */}
        <div className="bg-white rounded-card border border-ink-border overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-border"><h3 className="text-sm font-semibold text-ink">Top {TOP_N} opportunités</h3></div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-border bg-paper">
                <th className="text-left px-4 py-2.5 font-medium text-ink-muted">#</th>
                <th className="text-left px-4 py-2.5 font-medium text-ink-muted">Mot-clé</th>
                <th className="text-left px-4 py-2.5 font-medium text-ink-muted">Cluster</th>
                <th className="text-right px-4 py-2.5 font-medium text-ink-muted">Demande</th>
                <th className="text-right px-4 py-2.5 font-medium text-ink-muted">Capture</th>
                <th className="text-right px-4 py-2.5 font-medium text-ink-muted">Score</th>
                <th className="text-right px-4 py-2.5 font-medium text-ink-muted">Valeur perdue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-border/30">
              {topItems.map((item, idx) => (
                <tr key={item.keyword_id} onClick={() => setSelectedKw(item)} className="cursor-pointer hover:bg-paper transition-colors">
                  <td className="px-4 py-2.5 text-ink-faint font-mono">{idx + 1}</td>
                  <td className="px-4 py-2.5 font-medium text-ink">{item.keyword}</td>
                  <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1 text-xs text-ink-muted"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: getColor(item.cluster_group) }} />{item.cluster_group ?? '—'}</span></td>
                  <td className="px-4 py-2.5 text-right text-ink">{fmtCompact.format(item.demand)}</td>
                  <td className="px-4 py-2.5 text-right"><span className={cn('font-medium', item.capture_rate < 0.15 ? 'text-semantic-lost' : item.capture_rate < 0.30 ? 'text-semantic-conf-med' : 'text-semantic-capture')}>{fmtPct.format(item.capture_rate)}</span></td>
                  <td className="px-4 py-2.5 text-right font-bold text-ink">{item.opportunity_score.toFixed(1)}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-semantic-lost">{fmtEur.format(item.lost_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedKw && <OpportunityDrilldown item={selectedKw} onClose={() => setSelectedKw(null)} />}
      </div>
    </MarketTabsLayout>
  )
}
